import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { InlineSvgComponent } from '../../../../components/inline-svg/inline-svg.component';
import {
  UploadProgressMessage,
  UploadResponse
} from '../../../../models/project-upload.model';
import { Project } from '../../../../models/project.module';
import { TruncatePipe } from '../../../../pipes/truncate.pipe';
import { ProjectUploadService } from '../../../../services/project-upload.service';
import { ToastNotificationService } from '../../../../services/toast-notification.service';
import { findFirstNonEmptyString } from '../../../../utils';
import { MapCommunicatorService } from '../../map-communicator.service';
import { ErrorInFilesDialogComponent } from '../error-in-files-dialog/error-in-files-dialog.component';
import { MismatchInFilesDialogComponent } from '../mismatch-in-files-dialog/mismatch-in-files-dialog.component';

@Component({
  selector: 'frmg-file-processing-status',
  templateUrl: './file-processing-status.component.html',
  styleUrls: ['./file-processing-status.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, MatTooltip, InlineSvgComponent, MatButton, TruncatePipe]
})
export class FileProcessingStatusComponent
implements OnInit, OnChanges, OnDestroy
{
  @Input() projectId!: string;
  @Input() rootProjectId!: string;
  @Input() uploads: UploadResponse[] = [];

  @Output() importMore = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() goToFolder = new EventEmitter<Project>();

  progressMap: Record<string, UploadProgressMessage> = {};
  private socketSub?: Subscription;

  constructor(
    private uploadService: ProjectUploadService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private notificationService: ToastNotificationService,
    private mapCommunicatorService: MapCommunicatorService
  ) {}

  ngOnInit(): void {
    if (this.projectId) {
      this.initSocketListener();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['projectId']?.previousValue &&
      changes['projectId'].currentValue !== changes['projectId'].previousValue
    ) {
      this.initSocketListener();
    }
  }

  ngOnDestroy(): void {
    this.socketSub?.unsubscribe();
  }

  private initSocketListener(): void {
    this.socketSub?.unsubscribe();

    this.socketSub = this.uploadService
      .listenUploadMessages()
      .subscribe((msg) => {
        if (msg.project_id !== this.projectId) return;

        const upload = this.uploads.find((u) => u.upload_id === msg.upload_id);
        if (!upload) return;

        this.progressMap[msg.upload_id] = {
          ...this.progressMap[msg.upload_id],
          ...msg
        };

        // Map old status to new status format
        if (msg.status === 'in_progress') {
          upload.status = 'processing';
        } else if (msg.status === 'completed') {
          upload.status = 'completed';
        } else if (msg.status === 'failed') {
          upload.status = 'failed';
        }

        if (msg.status?.toLowerCase() === 'completed') {
          this.mapCommunicatorService.mapCommunicator.next('refresh_list');
        }

        this.cdr.detectChanges();
      });
  }

  getUploadStatus(upload: UploadResponse): string {
    return this.progressMap[upload.upload_id]?.status || upload.status;
  }

  getUploadPercentage(upload: UploadResponse): number {
    // Calculate percentage based on valid_records vs total_records
    if (upload.total_records > 0) {
      return Math.round((upload.valid_records / upload.total_records) * 100);
    }
    return this.progressMap[upload.upload_id]?.percentage ?? 0;
  }

  removeFileUpload(upload: UploadResponse): void {
    this.uploadService
      .deleteUpload(this.projectId, upload.upload_id)
      .subscribe({
        next: () => {
          this.notificationService.showNotification({
            type: 'success',
            message: 'Successfully deleted file upload.'
          });
          this.refreshUploads();
        },
        error: (error) => {
          this.notificationService.showNotification({
            type: 'error',
            message:
              findFirstNonEmptyString(error.error) ||
              'File upload delete failed'
          });
        }
      });
  }

  onImportMoreFilesClick(event: Event): void {
    event?.stopPropagation();
    this.importMore.emit();
  }

  showErrorDialog(file: UploadResponse): void {
    this.dialog
      .open(ErrorInFilesDialogComponent, {
        data: { projectId: this.projectId, file }
      })
      .afterClosed()
      .subscribe(() => this.refreshUploads());
  }

  showMismatchDialog(file: UploadResponse): void {
    this.dialog
      .open(MismatchInFilesDialogComponent, {
        data: { projectId: this.projectId, file }
      })
      .afterClosed()
      .subscribe(() => this.refreshUploads());
  }

  private refreshUploads(): void {
    this.uploadService
      .getUploads(this.projectId, { root_project: this.rootProjectId })
      .subscribe({
        next: (res) => {
          this.uploads = res.uploads;
          this.cdr.markForCheck();
        },
        error: (_err) => {
          // Removed console.error for production
        }
      });
  }

  close(): void {
    this.onClose.emit();
  }

  getFileFolderPath(file: UploadResponse): string {
    // Since the new model doesn't have a project object, we'll use the folder_id as the path
    return file.folder_id;
  }

  getFolderPathParts(path: string): (string | 'icon')[] {
    const segments = path.replace(/^root\//, '').split('/');
    if (segments.length === 2) {
      return [segments[0], 'icon', segments[1]];
    } else if (segments.length > 2) {
      return [
        segments[0],
        'icon',
        '...',
        'icon',
        segments[segments.length - 1]
      ];
    }
    return [segments[0]];
  }

  onFolderClick(file: UploadResponse, isLast: boolean): void {
    if (isLast) {
      // Create a mock project object from the upload data
      const mockProject: Project = {
        id: file.folder_id,
        name: file.filename,
        path: file.folder_id,
        is_project: false,
        description: '',
        project_id: file.project_id,
        // Add all required fields with default values
        reports_count: '0',
        downloads_count: '0',
        uploads_count: '0',
        parent: 0,
        team: {
          id: 0,
          name: '',
          slug: '',
          membership: {
            role: ''
          }
        },
        user: {
          id: '',
          email: '',
          first_name: '',
          last_name: '',
          full_name: '',
          teams: [],
          user_id: ''
        },
        children: [],
        is_root: false,
        shared_with: [],
        is_archived: false,
        skipped_paths: [],
        items: [],
        effective_permissions: {
          can_edit: false,
          can_delete: false,
          can_update: false,
          can_read: true
        },
        is_owned_by_current_user: true,
        parent_id: '',
        geometry_stats: {
          total_count: 0
        },
        shares: [],
        bounding_box: null,
        tenant_id: '',
        team_id: '',
        created_at: ''
      };
      this.goToFolder.emit(mockProject);
    }
  }
}
